import React, { createContext, useContext, useState, ReactNode } from 'react'
import { Button, Modal, Typography } from '@mui/material'
import { ModalActions, ModalContentContainer, ModalContainer, ModalContainerBody, ModalContent, ModalContentText, ModalHeaderText, ModalIconContainer } from './modal-provider.styles'

type ModalButton = {
	label: string,
	onClick?: () => void,
	variant?: 'text' | 'outlined' | 'contained',
	color?: 'primary' | 'secondary' | 'error',
}

export type ModalConfig = {
	title?: string,
	headerText?: string,
	icon?: ReactNode,
	content?: string,
	contentComponent?: ReactNode,
	buttons?: ModalButton[],
}

type ModalContextType = {
	showModal: ( config: ModalConfig ) => void,
	hideModal: () => void,
}

const ModalContext = createContext<ModalContextType | undefined>( undefined )

export const ModalProvider: React.FC<{ children: ReactNode }> = ( { children } ) => {
	const [ modalConfig, setModalConfig ] = useState<ModalConfig | null>( null )

	const showModal = ( config: ModalConfig ) => {
		setModalConfig( config )
	}

	const hideModal = () => {
		setModalConfig( null )
	}

	return (
		<ModalContext.Provider value={{ showModal, hideModal }}>
			{children}
			<Modal
				open={!!modalConfig}
				onClose={hideModal}
				title={modalConfig?.title}
			>
				<ModalContainer>
					<ModalContainerBody>
						{modalConfig?.icon && <ModalIconContainer>{modalConfig.icon}</ModalIconContainer>}
						<ModalContentContainer>
							<ModalHeaderText>
								{modalConfig?.headerText}
							</ModalHeaderText>
							<ModalContent>
								{modalConfig?.contentComponent ? (
									modalConfig.contentComponent
								) : (
									<ModalContentText>
										{modalConfig?.content}
									</ModalContentText>
								)}
							</ModalContent>
							{modalConfig?.buttons && (
								<ModalActions>
									{modalConfig.buttons.map( ( button ) => (
										<Button
											key={button.label}
											variant={button.variant ?? 'contained'}
											color={button.color ?? 'primary'}
											onClick={() => {
												button.onClick?.()
												hideModal()
											}}
										>
											<Typography>
												{button.label}
											</Typography>
										</Button>
									) )}
								</ModalActions>
							)}
						</ModalContentContainer>
					</ModalContainerBody>
				</ModalContainer>
			</Modal>
		</ModalContext.Provider>
	)
}

export const useModal = () => {
	const context = useContext( ModalContext )
	if ( !context ) {
		throw new Error( 'useModal must be used within a ModalProvider' )
	}
	return context
}
